export const QTY_DECIMAL_PLACES = 3;

/**
 * Parse user-entered quantity string — preserves up to 3 decimal places without rounding.
 * e.g. "1.123" stays 1.123, "1.12" stays 1.12 (not coerced to 1.120).
 */
export function parseQtyInput(value: string): number {
  const trimmed = value.trim();
  if (!trimmed || trimmed === '.') return 0;
  if (!/^\d+(\.\d{0,3})?$/.test(trimmed)) {
    const n = Number(trimmed);
    return Number.isFinite(n) ? coerceQty3(n) : 0;
  }
  const [wholePart, fractionPart = ''] = trimmed.split('.');
  const whole = Number(wholePart) || 0;
  if (!fractionPart) return whole;
  const fracLen = Math.min(fractionPart.length, QTY_DECIMAL_PLACES);
  const frac = Number(fractionPart.slice(0, QTY_DECIMAL_PLACES));
  return whole + frac / 10 ** fracLen;
}

/**
 * Normalize API/calculated floats to at most 3 dp (float noise only).
 * Do not use on user string input — use parseQtyInput instead.
 */
export function coerceQty3(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  const factor = 10 ** QTY_DECIMAL_PLACES;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

/** @deprecated Prefer parseQtyInput for user input and coerceQty3 for API values. */
export const roundQty3 = coerceQty3;

export function formatQty3(value: number | null | undefined): string {
  return coerceQty3(value ?? 0).toFixed(QTY_DECIMAL_PLACES);
}

/** Allow empty input or up to 3 decimal places while typing. */
export function isPartialQtyInput(value: string): boolean {
  return value === '' || /^\d*\.?\d{0,3}$/.test(value);
}

/** Prefer pending text input over stored numeric state (e.g. before blur / on save). */
export function resolveQtyFromInput(
  pendingInput: string | undefined,
  storedQty: number,
): number {
  if (pendingInput !== undefined && pendingInput.trim() !== '') {
    return parseQtyInput(pendingInput);
  }
  return storedQty;
}
